/**
 * Portal Feedback API Client
 *
 * Handles operations for feedback CRUD and approval with Cognito authentication.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to feedback endpoint.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

// ==================== Feedback API ====================

/**
 * List feedback for a course group filtered by role.
 *
 * @param {string} courseGroupId - Course group ID
 */
export const listFeedback = async (courseGroupId, viewAs = null) => {
  const data = { courseGroupId };
  if (viewAs) data.viewAs = viewAs;
  const result = await apiRequest('list', data);
  return result.feedbacks || [];
};

/**
 * List feedback for a specific student.
 *
 * @param {string} studentId - Student ID
 */
export const listForStudent = async (studentId) => {
  const result = await apiRequest('listForStudent', { studentId });
  return result.feedbacks || [];
};

/**
 * List pending approval feedback.
 * Admin roles only.
 */
export const listPending = async () => {
  const result = await apiRequest('listPending', {});
  return result.feedbacks || [];
};

/**
 * Create feedback.
 * Teachers only.
 *
 * @param {Object} data - Feedback data
 * @param {string} data.courseGroupId - Course group ID
 * @param {string} data.type - 'group' or 'individual'
 * @param {string} data.studentId - Student ID (required for individual)
 * @param {number} data.term - Term number (0=Special, 1-4). Snapshotted with year from the course group.
 * @param {number} data.week - Week number
 * @param {string} data.content - Feedback content
 */
export const createFeedback = async (data) => {
  const result = await apiRequest('create', data);
  return result.feedback;
};

/**
 * Update feedback.
 * Teachers can update own unpublished feedback.
 *
 * @param {string} feedbackId - Feedback ID
 * @param {Object} updates - Fields to update { content, week, term }
 */
export const updateFeedback = async (feedbackId, updates) => {
  const result = await apiRequest('update', { feedbackId, ...updates });
  return result.feedback;
};

/**
 * Delete feedback.
 *
 * @param {string} feedbackId - Feedback ID
 */
export const deleteFeedback = async (feedbackId) => {
  const result = await apiRequest('delete', { feedbackId });
  return result;
};

/**
 * Approve group feedback.
 * Admin roles only.
 *
 * @param {string} feedbackId - Feedback ID
 */
export const approveFeedback = async (feedbackId) => {
  const result = await apiRequest('approve', { feedbackId });
  return result.feedback;
};

/**
 * Reject group feedback with reason.
 * Admin roles only.
 *
 * @param {string} feedbackId - Feedback ID
 * @param {string} rejectionReason - Reason for rejection
 */
export const rejectFeedback = async (feedbackId, rejectionReason) => {
  const result = await apiRequest('reject', { feedbackId, rejectionReason });
  return result.feedback;
};

/**
 * Update feedback content and approve in one action.
 * Admin roles only.
 *
 * @param {string} feedbackId - Feedback ID
 * @param {string} content - Updated content (optional, if empty keeps original)
 */
export const updateAndApproveFeedback = async (feedbackId, content) => {
  const result = await apiRequest('updateAndApprove', { feedbackId, content });
  return result.feedback;
};

// ==================== Image Upload ====================

/**
 * S3 base URL for feedback images. Use with getFeedbackImageUrl().
 */
export const FEEDBACK_IMAGE_BASE_URL = 'https://uplus-portal-feedback-images.s3.ap-southeast-2.amazonaws.com';

/**
 * Construct full image URL from S3 key.
 */
export const getFeedbackImageUrl = (s3Key) => `${FEEDBACK_IMAGE_BASE_URL}/${s3Key}`;

/**
 * Get presigned URLs for feedback image uploads.
 * Can be called before feedback creation (pre-upload flow).
 *
 * @param {string} courseGroupId - Course group ID
 * @param {string} feedbackId - Feedback ID (can be pre-generated UUID)
 * @param {Array} images - Array of {fileName, contentType}
 * @returns {Array} Array of {index, uploadUrl, s3Key, contentType}
 */
export const getImageUploadUrls = async (courseGroupId, feedbackId, images) => {
  const result = await apiRequest('getImageUploadUrls', { courseGroupId, feedbackId, images });
  return result.uploadUrls || [];
};

/**
 * Upload a single image to S3 using presigned URL.
 *
 * @param {string} uploadUrl - Presigned S3 URL
 * @param {File} file - Image file to upload
 * @param {string} contentType - Content-Type matching the presigned URL
 */
export const uploadFeedbackImage = async (uploadUrl, file, contentType) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file
  });
  if (!response.ok) {
    throw new Error(`Failed to upload image: ${response.status}`);
  }
  return true;
};

// ==================== Constants ====================

/**
 * Feedback types
 */
export const FEEDBACK_TYPES = {
  GROUP: 'group',
  INDIVIDUAL: 'individual'
};

/**
 * Approval status
 */
export const APPROVAL_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

/**
 * Get approval status display info
 */
export const getApprovalStatusInfo = (status) => {
  switch (status) {
    case APPROVAL_STATUS.PENDING:
      return { label: 'Pending', color: 'warning' };
    case APPROVAL_STATUS.APPROVED:
      return { label: 'Approved', color: 'success' };
    case APPROVAL_STATUS.REJECTED:
      return { label: 'Rejected', color: 'danger' };
    default:
      return { label: status, color: 'secondary' };
  }
};

/**
 * Get feedback type display info
 */
export const getFeedbackTypeInfo = (type) => {
  switch (type) {
    case FEEDBACK_TYPES.GROUP:
      return { label: 'Group', color: 'primary', icon: 'bi-people' };
    case FEEDBACK_TYPES.INDIVIDUAL:
      return { label: 'Individual', color: 'info', icon: 'bi-person' };
    default:
      return { label: type, color: 'secondary', icon: 'bi-chat' };
  }
};

const feedbackPortalApi = {
  listFeedback,
  listForStudent,
  listPending,
  createFeedback,
  updateFeedback,
  deleteFeedback,
  approveFeedback,
  rejectFeedback,
  updateAndApproveFeedback,
  getImageUploadUrls,
  uploadFeedbackImage,
  getFeedbackImageUrl,
  FEEDBACK_IMAGE_BASE_URL,
  FEEDBACK_TYPES,
  APPROVAL_STATUS,
  getApprovalStatusInfo,
  getFeedbackTypeInfo
};

export default feedbackPortalApi;
